import { useEffect } from "react";
import { devLog } from "../../../utils/devLog";
import { useStyles } from "../../../context/StyleContext";
import { CALLBACKS, triggerCallback } from "../../../utils/commonUtils";
import { useTranslation } from "../../../hooks/useTranslation";
import { useCheckout } from "../../../context/CheckoutContext";
import { useIframeResize } from "../../../hooks/useIframeResize";
import PagbrasilCardForm from "./PagbrasilCardForm";

/**
 * Altruon (Embedded) PagBrasil Card Form Wrapper
 * 
 * Wraps PagbrasilCardForm for use in embedded/iframe checkout scenarios.
 * Handles parent iframe communication and callback triggering.
 */
const AltruonPagbrasilCardForm = ({
  formData,
  itemDetails,
  billingPlatformId,
  routingEntries,
  planId,
  platformItemDetails,
  checkoutPageConfDto,
  checkoutProcessDto,
  tenantId,
}) => {
  const queryParams = new URLSearchParams(window.location.search);
  const sessionId = queryParams.get("session_id");
  const { styles } = useStyles();
  const { t } = useTranslation();
  const { triggerPaymentEvent } = useCheckout();
  
  // Enable dynamic iframe resizing for embedded checkout
  useIframeResize({ enabled: true, padding: 40 });

  // Listen for messages from parent window
  useEffect(() => {
    const handleMessage = (event) => {
      if (event?.data?.type === "makePayment") {
        devLog("makePayment handler called for PagBrasil Card");
        triggerPaymentEvent();
      }

      if (event?.data?.type === "cardPaymentCompleted") {
        devLog("Card payment completed");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [triggerPaymentEvent]);

  /**
   * Handle card data changes - notify parent window
   */
  const handleCardDataChange = (cardData) => {
    window.parent.postMessage(
      {
        type: "updateCardData",
        data: cardData,
      },
      "*"
    );
  };

  /**
   * Handle successful payment
   */
  const handlePaymentSuccess = (response, latestSession) => {
    devLog("PagBrasil Card payment success:", response);
  };

  /**
   * Handle callbacks to parent/Altruon system
   */
  const onParentCallback = (event) => {
    switch (event.type) {
      case "finalizeFormData":
        // Notify parent of finalized form data
        window.parent.postMessage(event, "*");
        break;

      case "actionRequired":
        // 3DS or redirect required
        triggerCallback(CALLBACKS.ACTION_REQUIRED, {
          actionType: event.action || "REDIRECT",
          redirectUrl: event.url,
          message: "Payment requires additional action...",
        });
        break;

      case "success":
        // Payment successful
        triggerCallback(CALLBACKS.SUCCESS, {
          transactionId: event.transactionId,
          message: "Payment successfully processed.",
        });
        break;

      case "failure":
      default:
        // Payment failed
        triggerCallback(CALLBACKS.FAILURE, {
          status: "failed",
          message: event.message || t("messages.error.somethingWrong"),
        });
        break;
    }
  };

  return (
    <PagbrasilCardForm
      formData={formData}
      itemDetails={itemDetails}
      billingPlatformId={billingPlatformId}
      routingEntries={routingEntries}
      planId={planId}
      platformItemDetails={platformItemDetails}
      checkoutPageConfDto={checkoutPageConfDto}
      checkoutProcessDto={checkoutProcessDto}
      styles={styles}
      sessionId={sessionId}
      isEmbeddedCheckout={true}
      onCardDataChange={handleCardDataChange}
      onPaymentSuccess={handlePaymentSuccess}
      onParentCallback={onParentCallback}
      tenantId={tenantId}
    />
  );
};

export default AltruonPagbrasilCardForm;
